"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getAuthTokenForEndpoint = void 0;
const contentTypes = require("./content-types");
const registry_call_1 = require("./registry-call");
const needle_1 = require("./needle");
const realm_validation_1 = require("./realm-validation");
const options_1 = require("./options");
/**
 * Retrieves an authentication token for a specific registry endpoint.
 *
 * Handles the OAuth2-based bearer token acquisition flow used by Docker Registry V2.
 * First attempts to access the endpoint without authentication. If a 401 response is received,
 * parses the www-authenticate challenge header and obtains a bearer token from the specified
 * authentication endpoint.
 *
 * Authentication mechanisms supported:
 * - No authentication required (returns null)
 * - Basic authentication (returns null, handled by HTTP client)
 * - Bearer token (returns token)
 *
 * @param registryBase - The base URL of the container registry
 * @param endpoint - The registry endpoint to authenticate for (e.g., "/v2/")
 * @param username - Username for authentication
 * @param password - Password for authentication
 * @param reqOptions - Optional request configuration options
 *
 * @returns Authentication token string if bearer token is required, null if no authentication is needed
 *
 * @throws {Error} When credentials are invalid
 * @throws {Error} When registry instance is deactivated (e.g., Artifactory redirect)
 * @throws {NeedleWrapperException} When HTTP errors occur (except 401)
 *
 * @see https://docs.docker.com/registry/spec/auth/token/
 */
async function getAuthTokenForEndpoint(registryBase, endpoint, username, password, reqOptions = {}, observer) {
    var _a, _b;
    const opts = (0, options_1.cloneOptions)(reqOptions);
    const reqConfig = (0, registry_call_1.buildUnauthenticatedV2RequestConfig)(registryBase, endpoint, contentTypes.JSON, opts);
    try {
        await (0, needle_1.needleWrapper)(reqConfig);
        return null;
    }
    catch (err) {
        // check if artifactory is redirecting to landing page (deactivated instance, see #incident-2244-test-jfrog-artifactory-inactive for more details)
        if (err.statusCode === 302 &&
            ((_b = (_a = err.headers) === null || _a === void 0 ? void 0 : _a.location) === null || _b === void 0 ? void 0 : _b.includes("landing.jfrog.com"))) {
            throw new Error(`Artifactory instance appears to be deactivated (redirected to ${err.headers.location})`);
        }
        if (err.statusCode !== 401) {
            throw err;
        }
        const challengeHeaders = err.headers["www-authenticate"];
        if (!challengeHeaders) {
            return null;
        }
        const [authBase, service, scope] = (0, registry_call_1.parseChallengeHeaders)(challengeHeaders);
        if (!authBase) {
            return null;
        }
        else {
            // Bearer token
            // Validate realm URL against reqConfig.uri as fallback when registryBase is empty
            const parsedReqUri = new URL(reqConfig.uri);
            const effectiveHost = registryBase ? registryBase : parsedReqUri.hostname;
            (0, realm_validation_1.assertValidRealm)(effectiveHost, authBase, parsedReqUri.protocol, opts, observer);
            const token = await (0, registry_call_1.getToken)(registryBase, authBase, service, scope, username, password, opts);
            return token;
        }
    }
}
exports.getAuthTokenForEndpoint = getAuthTokenForEndpoint;
//# sourceMappingURL=get-auth-token-for-endpoint.js.map